import { ReactNode } from "react";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
} from "@chakra-ui/react";
import { SubscriptionStatus } from "__generated__/serverGql/graphql";
import { APP_NAME } from "utils/config";
import {
  COLOR_TO_ALERT_STATUS,
  SUBSCRIPTION_STATUS_CONFIG,
} from "utils/subscriptionStatusConfig";

type Props = {
  status: SubscriptionStatus | null;
};

const UPDATE_PAYMENT_MESSAGE =
  "Use the billing portal below to update your payment method.";

const START_NEW_MESSAGE = `Start a new subscription below to continue using ${APP_NAME}.`;

const STATUS_DESCRIPTIONS: Omit<
  Record<SubscriptionStatus, ReactNode>,
  SubscriptionStatus.ACTIVE
> = {
  [SubscriptionStatus.PAST_DUE]: (
    <>
      Your latest payment failed and we’re automatically retrying. Please update
      your payment method to avoid service interruption.{" "}
      {UPDATE_PAYMENT_MESSAGE}
    </>
  ),
  [SubscriptionStatus.UNPAID]: (
    <>
      Your subscription has unpaid invoices and service access has been revoked.{" "}
      {UPDATE_PAYMENT_MESSAGE}
    </>
  ),
  [SubscriptionStatus.CANCELED]: (
    <>Your subscription has been canceled. {START_NEW_MESSAGE}</>
  ),
  [SubscriptionStatus.INCOMPLETE]: (
    <>
      Your first payment requires action within 23 hours. Complete the payment
      process to activate your subscription. {UPDATE_PAYMENT_MESSAGE}
    </>
  ),
  [SubscriptionStatus.INCOMPLETE_EXPIRED]: (
    <>Your initial payment window has expired. {START_NEW_MESSAGE}</>
  ),
  [SubscriptionStatus.TRIALING]: (
    <>Your subscription is in a trial period with full access.</>
  ),
  [SubscriptionStatus.PAUSED]: (
    <>
      Your trial ended without a payment method. Add a payment method to resume
      your subscription. {UPDATE_PAYMENT_MESSAGE}
    </>
  ),
};

export default function SubscriptionStatusAlert({ status }: Props) {
  if (!status || status === SubscriptionStatus.ACTIVE) {
    return null;
  }

  const config = SUBSCRIPTION_STATUS_CONFIG[status];
  const description = STATUS_DESCRIPTIONS[status];

  if (!config || !description) {
    return null;
  }

  return (
    <Alert status={COLOR_TO_ALERT_STATUS[config.colorScheme]} my={8}>
      <AlertIcon />
      <Box>
        <AlertTitle>{config.title}</AlertTitle>
        <AlertDescription>{description}</AlertDescription>
      </Box>
    </Alert>
  );
}
